import React from 'react';
import { MethydTargetInfo } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { isNonEmpty } from '@ais-ui/utils';
import { Separator, TruncatedText } from '@ais-ui/ui-core';
import { LastOriginDisplay } from './LastOriginDisplay';

interface IdentificationItemProps {
  label: string;
  value: string;
}

interface IdentificationProps {
  methydInfo: MethydTargetInfo;
}

const IdentificationItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div className={'flex flex-col mt-1 min-w-0'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <TruncatedText
        text={isNonEmpty(value) ? value : 'Unknown'}
        className="text-xs select-text"
        maxWidth="100%"
      />
    </div>
  );
};

const Identification = ({ methydInfo }: IdentificationProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  return (
    <div className="grid grid-cols-2 gap-1">
      <IdentificationItem
        label={messages.targetDetailsMetHydMmsiLabel}
        value={methydInfo.metHydMmsi}
      />
      <IdentificationItem
        label={messages.targetDetailsLocationLabel}
        value={methydInfo.location}
      />
      <IdentificationItem
        label={messages.targetDetailsMetHydTimestampLabel}
        value={formatDateTimeWithTimeZone(methydInfo.metHydTimestamp)}
      />
      <IdentificationItem
        label={messages.targetDetailsMessageLabel}
        value={methydInfo.message}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseLatLabel}
        value={methydInfo.metHydLatitude.toString()}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseLonLabel}
        value={methydInfo.metHydLongitude.toString()}
      />
      <IdentificationItem
        label={messages.targetDetailsReportTimestampLabel}
        value={formatDateTimeWithTimeZone(methydInfo.reportTimestamp)}
      />
      <IdentificationItem
        label={messages.targetDetailsMetHydLatitudeLabel}
        value={methydInfo.metHydLatitude}
      />
      <IdentificationItem
        label={messages.targetDetailsMetHydLongitudeLabel}
        value={methydInfo.metHydLongitude}
      />
    </div>
  );
};

interface AirDetailsProps {
  methydInfo: MethydTargetInfo;
}

interface AirDetailsItemProps {
  label: string;
  value: string;
}

const AirDetailsItem = ({ label, value }: AirDetailsItemProps) => {
  return (
    <div className={'flex flex-col mt-1 min-w-0'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <TruncatedText
        text={isNonEmpty(value) ? value : 'Unknown'}
        className="text-xs select-text"
        maxWidth="100%"
      />
    </div>
  );
};

const AirDetailsStatus = ({ methydInfo }: AirDetailsProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-1">
      <AirDetailsItem
        label={messages.targetDetailsAirPressureTendencyLabel}
        value={methydInfo.airPressureTendency}
      />
      <AirDetailsItem
        label={messages.targetDetailsAirPressureLabel}
        value={methydInfo.airPressure}
      />
      <AirDetailsItem
        label={messages.targetDetailsAirTemperatureLabel}
        value={methydInfo.airTemperature}
      />
      <AirDetailsItem
        label={messages.targetDetailsAverageWindSpeedLabel}
        value={methydInfo.averageWindSpeed}
      />
      <AirDetailsItem
        label={messages.targetDetailsDewPointLabel}
        value={methydInfo.dewPoint}
      />
      <AirDetailsItem
        label={messages.targetDetailsRelativeHumidityLabel}
        value={methydInfo.relativeHumidity}
      />
      <AirDetailsItem
        label={messages.targetDetailsWindDirectionLabel}
        value={methydInfo.windDirection}
      />
      <AirDetailsItem
        label={messages.targetDetailsWindGustDirectionLabel}
        value={methydInfo.windGustDirection}
      />
      <AirDetailsItem
        label={messages.targetDetailsWindGustLabel}
        value={methydInfo.windGust}
      />
    </div>
  );
};

interface WaterDetailsProps {
  methydInfo: MethydTargetInfo;
}

interface WaterDetailsItemProps {
  label: string;
  value: string;
}

const WaterDetailsItem = ({ label, value }: WaterDetailsItemProps) => {
  return (
    <div className={'flex flex-col mt-1 min-w-0'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <TruncatedText
        text={isNonEmpty(value) ? value : 'Unknown'}
        className="text-xs select-text"
        maxWidth="100%"
      />
    </div>
  );
};

const WaterDetailsStatus = ({ methydInfo }: WaterDetailsProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-1">
      <WaterDetailsItem
        label={messages.targetDetailsCurrentDirection2Label}
        value={methydInfo.currentDirection2}
      />
      <WaterDetailsItem
        label={messages.targetDetailsCurrentDirection3Label}
        value={methydInfo.currentDirection3}
      />
      <WaterDetailsItem
        label={messages.targetDetailsCurrentMeasuringLevel2Label}
        value={methydInfo.currentMeasuringLevel2}
      />
      <WaterDetailsItem
        label={messages.targetDetailsCurrentMeasuringLevel3Label}
        value={methydInfo.currentMeasuringLevel3}
      />
      <WaterDetailsItem
        label={messages.targetDetailsCurrentSpeed2Label}
        value={methydInfo.currentSpeed2}
      />
      <WaterDetailsItem
        label={messages.targetDetailsCurrentSpeed3Label}
        value={methydInfo.currentSpeed3}
      />
      <WaterDetailsItem
        label={messages.targetDetailsSalinityLabel}
        value={methydInfo.salinity}
      />
      <WaterDetailsItem
        label={messages.targetDetailsSignificantWaveHeightLabel}
        value={methydInfo.significantWaveHeight}
      />
      <WaterDetailsItem
        label={messages.targetDetailsSurfaceCurrentDirectionLabel}
        value={methydInfo.surfaceCurrentDirection}
      />
      <WaterDetailsItem
        label={messages.targetDetailsSurfaceCurrentSpeedIncludingTideLabel}
        value={methydInfo.surfaceCurrentSpeedIncludingTide}
      />
      <WaterDetailsItem
        label={messages.targetDetailsSwellDirectionLabel}
        value={methydInfo.swellDirection}
      />
      <WaterDetailsItem
        label={messages.targetDetailsSwellHeightLabel}
        value={methydInfo.swellHeight}
      />
      <WaterDetailsItem
        label={messages.targetDetailsSwellPeriodLabel}
        value={methydInfo.swellPeriod}
      />
      <WaterDetailsItem
        label={messages.targetDetailsWaterLevelIncludingTideLabel}
        value={methydInfo.waterLevelIncludingTide}
      />
      <WaterDetailsItem
        label={messages.targetDetailsWaterLevelTrendLabel}
        value={methydInfo.waterLevelTrend}
      />
      <WaterDetailsItem
        label={messages.targetDetailsWaterTemperatureLabel}
        value={methydInfo.waterTemperature}
      />
      <WaterDetailsItem
        label={messages.targetDetailsWaveDirectionLabel}
        value={methydInfo.waveDirection}
      />
      <WaterDetailsItem
        label={messages.targetDetailsWavePeriodLabel}
        value={methydInfo.wavePeriod}
      />
    </div>
  );
};

export const MethydDetailsPanel = ({
  methydInfo,
}: {
  methydInfo: MethydTargetInfo;
}) => {
  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">
            Identification and Basic Details
          </h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Identification methydInfo={methydInfo}></Identification>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Air Details</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <AirDetailsStatus methydInfo={methydInfo}></AirDetailsStatus>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Water Details</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <WaterDetailsStatus methydInfo={methydInfo}></WaterDetailsStatus>
      </div>

      {methydInfo.lastOrigin && (
        <LastOriginDisplay lastOrigin={methydInfo.lastOrigin} />
      )}
    </div>
  );
};
